import styles from './styles.module.css'

import { AlertDialog } from '@radix-ui/themes'
import clsx from 'clsx'
import React, { useState } from 'react'

import mixpanel from '@disco/api/mixpanel'
import { pushUINotification } from '@disco/utils/notifications'

import { Brief } from 'types/brief'

import {
  Box,
  Button as DsButton,
  Dialog,
  Flex as DsFlex,
  Link,
  Switch,
  Table,
  Text,
} from 'components/ds'
import Button from 'components/ui/button'

import { RecipientInput } from '../recipient-input'
import { AssignedBriefUrl, useAssignBriefUrl } from './use-assign-brief-url'

interface RecipientTag {
  id: string
  email?: string
  name?: string
  contactId?: number
  isValid: boolean
}

export interface AssignUrlTabState {
  picked: RecipientTag[]
}

interface AssignUrlTabProps {
  brief: Brief
  onClose?: () => void
  onManageAccess: () => void
  persistedStateRef: React.MutableRefObject<AssignUrlTabState | null>
}

const getShareUrl = (row: AssignedBriefUrl) =>
  row.short_url || row.brief_submission_url

export const AssignUrlTab = ({
  brief,
  onClose,
  onManageAccess,
  persistedStateRef,
}: AssignUrlTabProps) => {
  const {
    urls,
    isLoading,
    isAssigning,
    isTogglingContactId,
    error,
    assignUrl,
    toggleDisabled,
  } = useAssignBriefUrl(brief.id)

  const [picked, setPicked] = useState<RecipientTag[]>(
    () => persistedStateRef.current?.picked ?? [],
  )
  const [pendingDisable, setPendingDisable] = useState<AssignedBriefUrl | null>(
    null,
  )

  React.useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    persistedStateRef.current = { picked }
  }, [picked, persistedStateRef])

  const handleAdd = (email?: string, name?: string, contactId?: number) => {
    const trimmedEmail = email?.trim() || ''
    const trimmedName = name?.trim() || ''
    if (!trimmedEmail && !trimmedName && contactId == null) return

    // OR-match dedupe inside the setState updater so a burst of adds from a
    // mailing-list / brief pick sees each previous iteration's queued pill.
    setPicked(prev => {
      const emailKey = trimmedEmail.toLowerCase()
      const nameKey = trimmedName.toLowerCase()

      const duplicate = prev.some(r => {
        if (
          contactId != null &&
          r.contactId != null &&
          r.contactId === contactId
        ) {
          return true
        }

        if (emailKey && r.email && r.email.toLowerCase() === emailKey) {
          return true
        }

        // Name match only when neither side has an id — so two different
        // Alices from the dropdown can coexist.
        if (contactId == null && r.contactId == null) {
          const otherName = (r.name || '').toLowerCase()
          if (nameKey && otherName && nameKey === otherName) {
            return true
          }
        }

        return false
      })
      if (duplicate) {
        return prev
      }

      return [
        ...prev,
        {
          id: `${Date.now()}-${Math.random()}`,
          email: trimmedEmail || undefined,
          name: trimmedName || undefined,
          contactId,
          isValid: true,
        },
      ]
    })
  }

  const handleRemove = (id: string) => {
    setPicked(prev => prev.filter(r => r.id !== id))
  }

  const handleAssign = async () => {
    if (picked.length === 0 || isAssigning) return

    try {
      for (const recipient of picked) {
        const { row, duplicate } = await assignUrl({
          email: recipient.email,
          name: recipient.name,
          contactId: recipient.contactId,
        })
        mixpanel.sendPureEvent('Brief share URL assigned', {
          Context: 'Briefs',
          brief_id: brief.id,
          contact_id: row.contact_id,
          duplicate,
        })
      }
      setPicked([])
    } catch (err) {
      pushUINotification({
        message: err instanceof Error ? err.message : 'Failed to assign URL',
        level: 'error',
      })
    }
  }

  const handleCopy = async (row: AssignedBriefUrl) => {
    if (!row.link_enabled) {
      return
    }

    const url = getShareUrl(row)
    try {
      await navigator.clipboard.writeText(url)
      pushUINotification({ message: 'Link copied', level: 'success' })
      mixpanel.sendPureEvent('Brief share URL copied', {
        Context: 'Briefs',
        brief_id: brief.id,
        contact_id: row.contact_id,
      })
    } catch {
      pushUINotification({
        message: 'Failed to copy link',
        level: 'error',
      })
    }
  }

  const handleToggleRequested = async (row: AssignedBriefUrl) => {
    if (!row.link_enabled) {
      try {
        await toggleDisabled(row)
        mixpanel.sendPureEvent('Brief recipient link toggled', {
          Context: 'Briefs',
          brief_id: brief.id,
          bulk: false,
          enabled: true,
        })
      } catch {
        pushUINotification({
          message: 'Failed to enable link',
          level: 'error',
        })
      }
    } else {
      setPendingDisable(row)
    }
  }

  const handleConfirmDisable = async () => {
    if (!pendingDisable) {
      return
    }
    const row = pendingDisable
    setPendingDisable(null)

    try {
      await toggleDisabled(row)
      mixpanel.sendPureEvent('Brief recipient link toggled', {
        Context: 'Briefs',
        brief_id: brief.id,
        bulk: false,
        enabled: false,
      })
    } catch {
      pushUINotification({
        message: 'Failed to disable link',
        level: 'error',
      })
    }
  }

  const assignButtonText =
    picked.length > 1 ? `Assign ${picked.length} URLs` : 'Assign URL'

  return (
    <>
      <Box>
        <Box mb="3">
          <RecipientInput
            recipients={picked}
            onAdd={handleAdd}
            onRemove={handleRemove}
            aria-label="Assign URL recipients"
            allowCreateRecipient
            includeBlankEmails
          />
        </Box>

        <DsFlex mb="4" justify="end">
          <Button
            appearance="primary"
            onClick={handleAssign}
            disabled={picked.length === 0 || isAssigning}
            caption={assignButtonText}
            progressCaption="Generating"
            progressIndicator={isAssigning}
            className={styles.focusable}
          />
        </DsFlex>
      </Box>

      <Box className={styles.scrollArea}>
        {error && (
          <Box mb="3">
            <Text size="2" color="red">
              {error}
            </Text>
          </Box>
        )}

        {isLoading ? (
          <Box mb="3" p="4">
            <Text size="2" color="gray" align="center" as="div">
              Loading URLs…
            </Text>
          </Box>
        ) : urls.length > 0 ? (
          <Table.Root variant="surface" size="1">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>URL</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Active</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {urls.map(row => (
                <Table.Row key={row.contact_id}>
                  <Table.Cell>
                    <Text
                      size="2"
                      className={clsx(
                        styles.overflowCell,
                        !row.link_enabled && styles.disabledRow,
                      )}
                    >
                      {row.name}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text
                      size="2"
                      color="gray"
                      title={row.email}
                      className={clsx(
                        styles.overflowCell,
                        !row.link_enabled && styles.disabledRow,
                      )}
                    >
                      {row.email}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <DsButton
                      variant="soft"
                      color="blue"
                      size="1"
                      disabled={!row.link_enabled}
                      onClick={() => handleCopy(row)}
                      aria-label={`Copy URL for ${row.email}`}
                      className={styles.focusable}
                    >
                      Copy URL
                    </DsButton>
                  </Table.Cell>
                  <Table.Cell>
                    <Switch
                      checked={row.link_enabled}
                      onCheckedChange={() => handleToggleRequested(row)}
                      disabled={isTogglingContactId === row.contact_id}
                      aria-label={
                        row.link_enabled
                          ? `Disable link for ${row.email}`
                          : `Enable link for ${row.email}`
                      }
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        ) : (
          <Box mb="3" p="4">
            <Text size="2" color="gray" align="center" as="div">
              No URLs assigned yet. Pick a contact above to generate one.
            </Text>
          </Box>
        )}
      </Box>

      {(brief.status === 'sent' || brief.sent_status) && (
        <Box mt="4">
          <Link
            href="#"
            size="2"
            color="blue"
            onClick={onManageAccess}
            className={styles.focusable}
          >
            Manage brief access
          </Link>
        </Box>
      )}

      <DsFlex gap="3" justify="end" mt="4">
        <Dialog.Close>
          <DsButton
            variant="soft"
            color="gray"
            size="2"
            onClick={onClose}
            className={styles.focusable}
          >
            Done
          </DsButton>
        </Dialog.Close>
      </DsFlex>

      <AlertDialog.Root
        open={pendingDisable !== null}
        onOpenChange={open => {
          if (!open) setPendingDisable(null)
        }}
      >
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>Disable this link?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            The recipient will lose access via this URL. You can re-enable it
            later.
          </AlertDialog.Description>
          <DsFlex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <DsButton
                variant="soft"
                color="gray"
                className={styles.focusable}
              >
                Cancel
              </DsButton>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <DsButton
                variant="solid"
                color="red"
                onClick={handleConfirmDisable}
                className={styles.focusable}
              >
                Disable link
              </DsButton>
            </AlertDialog.Action>
          </DsFlex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </>
  )
}
